// src/pages/theater_staff/ShowList.jsx
import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import {
  VideoCameraOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { Table, Input, message, Tag, Empty, Typography, Button, Space, Tooltip } from 'antd';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const ShowList = () => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchShows(pagination.current, search);
  }, [pagination.current, search]);

  const fetchShows = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      let url = `show-management/theatre-shows/?page=${page}&page_size=${pagination.pageSize}`;
      if (searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await axiosInstance.get(url);

      const activeShows = (response.data.results || []).filter(
        show => show.status === 'Active'
      );

      setShows(activeShows);
      setPagination(prev => ({
        ...prev,
        current: page,
        total: response.data.count || 0,
      }));
    } catch (error) {
      message.error('Failed to load shows');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Movie',
      dataIndex: 'movie',
      key: 'movie',
      render: movie => <strong>{movie || '—'}</strong>,
    },
    {
      title: 'Screen',
      dataIndex: 'screen',
      key: 'screen',
      render: screen => screen || '—',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: date => (date ? new Date(date).toLocaleDateString('en-IN') : '—'),
    },
    {
      title: 'Time Slot',
      dataIndex: 'timeslot',
      key: 'timeslot',
      render: timeslot => timeslot || '—',
    },
    {
      title: 'Show Time',
      key: 'show_time',
      render: (_, record) => (
        <Text>
          <ClockCircleOutlined style={{ marginRight: 6 }} />
          {record.start_time} - {record.end_time}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => (
        <Tag color={status === 'Active' ? 'green' : 'volcano'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Space size="middle">
          {/* View Bookings */}
          <Tooltip title="View Bookings">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate(
                  `/theater/shows/${record.id}/${record.show_date_id}/${record.show_timeslot_id}`
                )
              }
            />
          </Tooltip>

          {/* View Ticket Snapshots */}
          <Tooltip title="View Ticket Snapshots">
            <Button
              type="link"
              icon={<CameraOutlined />}
              style={{ color: '#1890ff' }}
              onClick={() =>
                navigate(
                  `/theater/snapshots/${record.id}/${record.show_date_id}/${record.show_timeslot_id}`
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="dashboard">
      <PageHeading name="Show List" icon={<VideoCameraOutlined />} />

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Search by movie name"
          prefix={<SearchOutlined />}
          style={{ width: '240px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Active shows in <strong>your theatre</strong>
        </Text>
      </div>

      <Table
        dataSource={shows}
        columns={columns}
        rowKey="id" // Now safe: each show + timeslot combo has unique id + timeslot_id
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (page) => {
            setPagination(prev => ({ ...prev, current: page }));
          },
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active shows scheduled"
            />
          ),
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default ShowList;